/**
 * Centralized auth-cookie handling — the single place the `auth_token`
 * cookie is written, read, and cleared. Replaces the ~12 inline
 * `document.cookie = ...` sites (token-cookie-security project).
 *
 * SameSite=None preserves current behavior for cross-origin iframe
 * embeds; the planned tightening to Lax lands together with the iframe
 * postMessage auth path (token-cookie-security Design §4/§6) — flip
 * COOKIE_SAMESITE when that ships.
 */

import { jose } from '../deps.js'
import { acequiaReady, getUserId, getUserKeys } from '../acequia/acequia.js'
import { createUserToken } from './tokens.js'

const COOKIE_NAME = 'auth_token'
export const COOKIE_MAX_AGE = 30 * 24 * 60 * 60 // seconds — 30 days
const COOKIE_SAMESITE = 'None' // → 'Lax' once iframe postMessage auth ships

// In-memory token for cross-origin iframe embeds (postMessage handoff).
// When set, getAuthCookie() prefers it — pages inside third-party iframes
// increasingly cannot use cookies at all (Safari ITP, Firefox Total
// Cookie Protection). Populated by the future iframe auth listener.
let _embeddedToken = null

export function setAuthCookie(token) {
    document.cookie = `${COOKIE_NAME}=${token}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=${COOKIE_SAMESITE}; Secure;`
}

export function getAuthCookie() {
    if (_embeddedToken) return _embeddedToken
    const entry = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith(COOKIE_NAME + '='))
    return entry ? entry.slice(COOKIE_NAME.length + 1) : null
}

export function clearAuthCookie() {
    _embeddedToken = null
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

export function setEmbeddedToken(token) {
    _embeddedToken = token
}

export function getEmbeddedToken() {
    return _embeddedToken
}

/**
 * Verify a candidate token actually authenticates against this server.
 * Keys and userIds exist locally without any registration having
 * happened (acequiaReady creates them on first run), so a locally-minted
 * token proves nothing until the server accepts it — replacing a working
 * cookie with an unverifiable token would break the session (e.g. a
 * legacy device-token cookie on a legacy-mode domain).
 */
async function tokenAuthenticates(token, userId) {
    try {
        const resp = await fetch(`/auth/users/${encodeURIComponent(userId)}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        return resp.ok
    } catch (e) {
        return false // offline or unreachable — do not touch the cookie
    }
}

/**
 * Proactive session-cookie maintenance (token-cookie-security §2/§3).
 * Re-mints and rewrites the auth cookie when:
 * - the cookie's sub differs from the current identity (stale cookie
 *   surviving an IndexedDB reset — the Graydon failure shape)
 * - the token is past half-life (silent refresh, so expiry never
 *   surfaces to the user)
 * - the token has no exp (legacy eternal cookie — upgrade to expiring)
 *
 * Guard rails: no-op when there is no cookie (never self-provisions a
 * session), and the replacement is only written after it authenticates
 * against this server — local keys/ids do not imply registration here.
 *
 * @returns {Promise<'renewed'|'fresh'|'none'|'skipped'|'unregistered'>}
 */
export async function checkAuthCookieRenewal() {
    const token = getAuthCookie()
    if (!token) return 'none'

    await acequiaReady()
    if (!getUserKeys()) return 'skipped'

    let payload
    try {
        payload = jose.decodeJwt(token)
    } catch (e) {
        return 'skipped' // not a JWT — leave it alone
    }

    const userId = getUserId()
    if (!userId) return 'skipped'
    const now = Math.floor(Date.now() / 1000)

    let reason = null
    if (payload.sub && payload.sub !== userId) {
        console.warn('[cookies] auth cookie userId mismatch — refreshing token')
        reason = 'sub-mismatch'
    } else if (!payload.exp) {
        reason = 'no-exp'
    } else {
        const ttl = payload.iat ? payload.exp - payload.iat : COOKIE_MAX_AGE
        if (payload.exp - now < ttl / 2) reason = 'half-life'
    }
    if (!reason) return 'fresh'

    const newToken = await createUserToken({ domain: location.hostname })
    if (!(await tokenAuthenticates(newToken, userId))) {
        // Keys exist but this server doesn't know them (unregistered
        // identity, revoked key, or legacy-mode domain) — leave the
        // existing cookie alone.
        return 'unregistered'
    }
    setAuthCookie(newToken)
    console.log(`[cookies] auth cookie renewed (${reason})`)
    return 'renewed'
}